import React from "react";
import { Icon } from "rhombus-react";
import { STATUS_MAP, INCOMPLETE_DVAST_TOOLTIP, INCOMPLETE_IMPOSIUM_TOOLTIP } from "utils/constants";
import { useTooltipRebuild } from "hooks/useTooltipRebuild";
import {
	isIncomplete,
	isSocialCreative,
	isVASTAudioCreative,
	isSocialCarouselCreative,
	isImposiumCreative
} from "utils/common";
import { isDVAST } from "utils/CreativeActions";
import { tellSentry } from "utils/Rave";
import { Link } from "react-router-dom";
import classNames from "classnames";
import PropTypes from "prop-types";

import "./StatusColumn.css";

/**
 * Functional component that sets styling and html for the Status Column in Grids
 * @param {string} [value  = ""] -  approval status of creative
 * @param {Object} [dependentValues = {}] - { creativeId, libraryId, adType } || { statusMap:{} }
 * @param {boolean} [shouldShowLinks = true] - prop passed from CreativeTable.js for bulk asset upload.
 * @returns {JSX}
*/

export default function StatusColumn({ value, dependentValues, shouldShowLinks }) {
	let { statusMap = STATUS_MAP } = dependentValues
		, status = statusMap[value.toLowerCase()]
		, isSocial = isSocialCreative(dependentValues)
		, isCarousel = isSocialCarouselCreative(dependentValues)
		, isDynamicVast = isDVAST(dependentValues?.adType)
		, isImposium = isImposiumCreative(dependentValues)
		, isStatusIncomplete = isIncomplete({ approval: value })
		, specPath = isVASTAudioCreative(dependentValues) ? 'audio-specs' : 'video-specs'
		, path = `/library/${dependentValues?.libraryId}/modal/${specPath}/${dependentValues?.creativeId}/`
		, title = status?.label || ""
		, label = status?.label || "unknown"
		, dataTip = null
		, $icon = null
		, transcodingError = value === "TRANSCODING_ERROR"
		;

	// If for whatever reason we can't find the status in the mapper, let's use `Unknown`, and log something to the console
	if(!status){
		status = statusMap.unknown || STATUS_MAP.unknown ;
		tellSentry(`Unknown status type: ${value.toLowerCase()}`);
	}

	useTooltipRebuild(value);

	if(isSocial){
		if(transcodingError){
			title = label; // label is changing just for socials, so title needs to follow
			dataTip = isCarousel ? "One or more cards failed to process" : `Creative processing failed, <br/> please try again in a little bit,<br/> please reach out to support if the error persists`;
		}
	}

	if(isStatusIncomplete){
		$icon = <Icon name = {status.icon} />;
		title = null;
		[label] = label.split("-");

		if (isImposium) {
			dataTip = INCOMPLETE_IMPOSIUM_TOOLTIP;
		} else if (isDynamicVast) {
			dataTip = INCOMPLETE_DVAST_TOOLTIP;
		} else {
			dataTip = status.tooltipLabel;
		}
	}

	if(shouldShowLinks && transcodingError){
		if(!isSocial){ //HOPI-767
			return (
				<Link to={path}
					data-tip={dataTip} data-html={true} >
					<span className={`status-label ${status.labelClass}`} >{label}</span>
				</Link>
			);
		}
	}

	return (
		<span className={classNames('status-label', status.labelClass, { "is-social":isSocial })}
			title={title}
			data-tip={dataTip}
			data-html={true}
		>
			{$icon}
			{label}
		</span>)
	;
}

StatusColumn.defaultProps={
	value:           "",
	shouldShowLinks: true,
	dependentValues: {},
};

StatusColumn.propTypes = {
	value:           PropTypes.string.isRequired,
	shouldShowLinks: PropTypes.bool,
	dependentValues: PropTypes.object,
};
